import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components';

const TermsOfService: React.FC = () => {
  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms' },
    { id: 'service-description', title: '2. Service Description' },
    { id: 'account', title: '3. Account Registration' },
    { id: 'content', title: '4. Your Content' },
    { id: 'acceptable-use', title: '5. Acceptable Use' },
    { id: 'intellectual-property', title: '6. Intellectual Property' },
    { id: 'subscription', title: '7. Subscription and Billing' },
    { id: 'privacy', title: '8. Privacy' },
    { id: 'disclaimers', title: '9. Disclaimers' },
    { id: 'limitation', title: '10. Limitation of Liability' },
    { id: 'indemnification', title: '11. Indemnification' },
    { id: 'termination', title: '12. Termination' },
    { id: 'modifications', title: '13. Modifications to Terms' },
    { id: 'governing-law', title: '14. Governing Law' },
    { id: 'contact', title: '15. Contact Information' },
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="pt-20 pb-8 sm:pt-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 leading-tight text-slate-900">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-600">
            Last updated: January 31, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Table of Contents */}
            <div className="p-6 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Table of Contents</h2>
              <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm hover:underline"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>

            {/* Terms Content */}
            <div className="p-6 sm:p-8 prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed mb-8">
                Welcome to SystemDox. These Terms of Service ("Terms") govern your access to and use of
                the SystemDox platform, including our website, applications, and services (collectively,
                the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
              </p>

              <section id="acceptance" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  By creating an account, accessing, or using the Service, you acknowledge that you have
                  read, understood, and agree to be bound by these Terms and our Privacy Policy. If you
                  do not agree to these Terms, you may not access or use the Service.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  If you are using the Service on behalf of an organization, you represent and warrant
                  that you have the authority to bind that organization to these Terms, and "you" refers
                  to both you individually and that organization.
                </p>
              </section>

              <section id="service-description" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. Service Description</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  SystemDox provides an AI-powered platform for creating, managing, and maintaining
                  enterprise architecture documentation. Our Service includes:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Voice-to-documentation transcription and AI processing</li>
                  <li>Architecture documentation templates and management tools</li>
                  <li>Document storage, organization, and search capabilities</li>
                  <li>Collaboration features for teams</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to modify, suspend, or discontinue any aspect of the Service at
                  any time, with or without notice.
                </p>
              </section>

              <section id="account" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Account Registration</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  To access certain features of the Service, you must create an account. You agree to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized access or security breach</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  You must be at least 18 years old or the age of majority in your jurisdiction to
                  create an account and use the Service.
                </p>
              </section>

              <section id="content" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Your Content</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  "Your Content" refers to any data, text, audio, documents, or other materials you
                  upload, submit, or create through the Service. You retain ownership of Your Content,
                  subject to the limited rights you grant to us.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  By submitting Your Content to the Service, you grant SystemDox a worldwide,
                  non-exclusive, royalty-free license to use, process, store, and display Your Content
                  solely for the purpose of providing and improving the Service.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  You represent and warrant that you have all necessary rights to Your Content and that
                  Your Content does not violate any third-party rights or applicable laws.
                </p>
              </section>

              <section id="acceptable-use" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  You agree not to use the Service to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Violate any applicable laws, regulations, or third-party rights</li>
                  <li>Upload or transmit malicious code, viruses, or harmful content</li>
                  <li>Attempt to gain unauthorized access to any part of the Service</li>
                  <li>Interfere with or disrupt the Service or its infrastructure</li>
                  <li>Use the Service for any illegal, fraudulent, or unauthorized purpose</li>
                  <li>Reverse engineer, decompile, or attempt to extract source code</li>
                  <li>Resell, sublicense, or redistribute the Service without authorization</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to investigate and take appropriate action against any violation
                  of these Terms, including suspending or terminating your access to the Service.
                </p>
              </section>

              <section id="intellectual-property" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. Intellectual Property</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  The Service and its original content (excluding Your Content), features, and
                  functionality are owned by SystemDox and are protected by international copyright,
                  trademark, patent, trade secret, and other intellectual property laws.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our trademarks, service marks, and logos may not be used in connection with any
                  product or service without our prior written consent.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  The templates and documentation structures provided through the Service are licensed
                  to you for use within the Service. You may export and use generated documentation
                  for your internal business purposes.
                </p>
              </section>

              <section id="subscription" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Subscription and Billing</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Certain features of the Service require a paid subscription. By subscribing, you agree
                  to pay the applicable fees as described at the time of purchase.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
                  <li>Prices may change with notice; continued use constitutes acceptance</li>
                  <li>Refunds are provided in accordance with our refund policy</li>
                  <li>You are responsible for all applicable taxes</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  We may offer free trials or promotional pricing. Unless you cancel before the trial
                  ends, you will be charged the standard subscription fee.
                </p>
              </section>

              <section id="privacy" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Privacy</h2>
                <p className="text-slate-600 leading-relaxed">
                  Your privacy is important to us. Our collection and use of personal information is
                  governed by our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>,
                  which is incorporated into these Terms by reference. By using the Service, you consent
                  to the collection and use of information as described in our Privacy Policy.
                </p>
              </section>

              <section id="disclaimers" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Disclaimers</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We do not warrant that the Service will be uninterrupted, secure, or error-free.
                  AI-generated content may contain inaccuracies, and you are responsible for reviewing
                  and validating all output.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We are not responsible for any decisions made based on documentation generated through
                  the Service. You should independently verify all architectural decisions and documentation.
                </p>
              </section>

              <section id="limitation" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, SYSTEMDOX SHALL NOT BE LIABLE FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
                  NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR THE
                  SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE TWELVE
                  MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
              </section>

              <section id="indemnification" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
                <p className="text-slate-600 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless SystemDox, its affiliates, officers,
                  directors, employees, and agents from any claims, liabilities, damages, losses, and
                  expenses (including reasonable attorneys' fees) arising from or related to: (a) your
                  use of the Service; (b) Your Content; (c) your violation of these Terms; or (d) your
                  violation of any third-party rights.
                </p>
              </section>

              <section id="termination" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">12. Termination</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  You may terminate your account at any time by contacting us or through your account
                  settings. We may suspend or terminate your access to the Service at any time, with
                  or without cause, and with or without notice.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Upon termination, your right to use the Service ceases immediately. We may retain
                  Your Content for a reasonable period to allow you to export it, after which it may
                  be deleted. Provisions that by their nature should survive termination will survive.
                </p>
              </section>

              <section id="modifications" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">13. Modifications to Terms</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We reserve the right to modify these Terms at any time. If we make material changes,
                  we will notify you by email or by posting a notice on the Service prior to the changes
                  taking effect.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Your continued use of the Service after any modifications constitutes your acceptance
                  of the updated Terms. If you do not agree to the modified Terms, you must stop using
                  the Service.
                </p>
              </section>

              <section id="governing-law" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">14. Governing Law</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the
                  United Kingdom, without regard to its conflict of law provisions.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Any disputes arising from or relating to these Terms or the Service shall be resolved
                  exclusively in the courts located in England and Wales, and you consent to the
                  personal jurisdiction of such courts.
                </p>
              </section>

              <section id="contact" className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">15. Contact Information</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700 font-medium">SystemDox Legal</p>
                  <p className="text-slate-600">Email: legal@systemdox.com</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default TermsOfService;
